import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { Apis } from "../../../lib/Apis";
import Loader from "../../../Utils/loader";
import { FiSearch } from "react-icons/fi";

const PERMISSION_FETCH_LIMIT = 1000;

function RoleForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const roleState = location.state?.role;
  const mode = location.state?.mode || (id ? "edit" : "create");
  const isViewMode = mode === "view";

  const [formData, setFormData] = useState({
    name: roleState?.name || "",
    description: roleState?.description || "",
  });
  const [permissions, setPermissions] = useState(roleState?.permissions || []);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState(
    new Set((roleState?.permissions || []).map((permission) => permission.id)),
  );
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const title = useMemo(() => {
    if (isViewMode) return "View Role";
    return id ? "Edit Role" : "Create New Role";
  }, [id, isViewMode]);

  const fetchPermissions = useCallback(async () => {
    if (isViewMode) {
      return;
    }

    try {
      setIsLoadingPermissions(true);
      const response = await Apis.getPermissions(0, PERMISSION_FETCH_LIMIT, {
        status: true,
      });
      const payload = response.data.data;
      const fetchedPermissions = payload.permissions || [];
      const initialRolePermissions = roleState?.permissions || [];
      const unique = new Map();
      [...initialRolePermissions, ...fetchedPermissions].forEach((permission) =>
        unique.set(permission.id, permission),
      );
      setPermissions([...unique.values()]);
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to fetch permissions.",
        "error",
      );
    } finally {
      setIsLoadingPermissions(false);
    }
  }, [isViewMode, roleState?.permissions]);

  const fetchRole = useCallback(async () => {
    if (!id || roleState) {
      return;
    }

    try {
      setIsLoadingPermissions(true);
      const response = await Apis.getRoleById(id);
      const role = response.data.data;

      setFormData({
        name: role.name || "",
        description: role.description || "",
      });
      setPermissions(role.permissions || []);
      setSelectedPermissionIds(
        new Set((role.permissions || []).map((permission) => permission.id)),
      );
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to fetch role.",
        "error",
      );
    } finally {
      setIsLoadingPermissions(false);
    }
  }, [id, roleState]);

  useEffect(() => {
    if (isViewMode) {
      if (roleState) {
        setPermissions(roleState.permissions || []);
        setSelectedPermissionIds(
          new Set(
            (roleState.permissions || []).map((permission) => permission.id),
          ),
        );
        setIsLoadingPermissions(false);
        return;
      }

      fetchRole();
      return;
    }

    fetchPermissions();
  }, [fetchPermissions, fetchRole, isViewMode, roleState]);

  const [searchQuery, setSearchQuery] = useState("");

  const filteredPermissions = useMemo(() => {
    if (!searchQuery.trim()) return permissions;
    const query = searchQuery.toLowerCase();
    return permissions.filter((permission) => {
      const moduleName = (
        permission.module ||
        permission.resource ||
        ""
      ).toLowerCase();
      const actionName = (permission.action || "")
        .toLowerCase()
        .replaceAll("_", " ");
      return moduleName.includes(query) || actionName.includes(query);
    });
  }, [permissions, searchQuery]);

  const groupedPermissions = useMemo(() => {
    return filteredPermissions.reduce((groups, permission) => {
      const groupName = permission.module || permission.resource;
      if (!groups[groupName]) groups[groupName] = [];
      groups[groupName].push(permission);
      return groups;
    }, {});
  }, [filteredPermissions]);

  const togglePermission = (permissionId) => {
    if (isViewMode) return;
    setSelectedPermissionIds((prevSelected) => {
      const nextSelected = new Set(prevSelected);
      if (nextSelected.has(permissionId)) {
        nextSelected.delete(permissionId);
      } else {
        nextSelected.add(permissionId);
      }
      return nextSelected;
    });
  };

  const toggleGroup = (groupPermissions) => {
    if (isViewMode) return;
    const allSelected = groupPermissions.every((permission) =>
      selectedPermissionIds.has(permission.id),
    );
    setSelectedPermissionIds((prevSelected) => {
      const nextSelected = new Set(prevSelected);
      groupPermissions.forEach((permission) => {
        if (allSelected) {
          nextSelected.delete(permission.id);
        } else {
          nextSelected.add(permission.id);
        }
      });
      return nextSelected;
    });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      Swal.fire("Error", "Role name is required.", "error");
      return;
    }

    if (selectedPermissionIds.size === 0) {
      Swal.fire("Error", "Please select at least one permission.", "error");
      return;
    }

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      permissionIds: [...selectedPermissionIds],
    };

    try {
      setIsSubmitting(true);
      if (id) {
        await Apis.updateRole(id, payload);
        Swal.fire("Success", "Role updated successfully.", "success");
      } else {
        await Apis.createRole(payload);
        Swal.fire("Success", "Role created successfully.", "success");
      }
      navigate("/roles");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to save role.",
        "error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingPermissions && permissions.length === 0) {
    return <Loader message="Loading permissions..." />;
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-5">
        <h2 className="text-2xl font-bold">{title}</h2>
        <div className="flex items-center gap-2">
          {!isViewMode && (
            <button
              className="px-6 py-2 rounded bg-[#c91f32] text-white disabled:opacity-50 font-semibold hover:bg-[#a81a27] transition-all duration-200 text-sm"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : id ? "Update" : "Add"}
            </button>
          )}
          {/* <button
            className="btn btn-reset-filter"
            onClick={() => navigate("/roles")}
          >
            Back
          </button> */}
        </div>
      </div>

      <div className="bg-white rounded-lg pagination-shadow p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <div>
            <label className="block font-semibold mb-2">Role Name</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-primary"
              placeholder="Enter role name"
              value={formData.name}
              disabled={isViewMode}
              onChange={(event) =>
                setFormData({ ...formData, name: event.target.value })
              }
            />
          </div>
          <div>
            <label className="block font-semibold mb-2">Description</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-primary"
              placeholder="Enter description"
              value={formData.description}
              disabled={isViewMode}
              onChange={(event) =>
                setFormData({ ...formData, description: event.target.value })
              }
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-4 mt-6">
          <h3 className="font-semibold text-lg">Permissions:</h3>
          <div className="search-wrapper w-full sm:w-72">
            <input
              type="text"
              className="search-input"
              placeholder="Search permissions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <FiSearch className="search-icon" />
          </div>
        </div>
        <div className="border rounded-md overflow-hidden">
          {Object.keys(groupedPermissions).length === 0 ? (
            <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-md">
              No permissions found matching "{searchQuery}".
            </div>
          ) : (
            Object.entries(groupedPermissions).map(
              ([moduleName, groupPermissions]) => {
                const allSelected = groupPermissions.every((permission) =>
                  selectedPermissionIds.has(permission.id),
                );
                return (
                  <div
                    key={moduleName}
                    className={`grid grid-cols-1 ${isViewMode ? "md:grid-cols-[170px_1fr]" : "md:grid-cols-[170px_130px_1fr]"} gap-3 border-b last:border-b-0 p-4`}
                  >
                    <div className="font-medium text-gray-700">
                      {moduleName}
                    </div>
                    {!isViewMode && (
                      <label className="inline-flex items-center gap-2 text-gray-700">
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-[#c91f32]"
                          checked={allSelected}
                          disabled={isViewMode}
                          onChange={() => toggleGroup(groupPermissions)}
                        />
                        Select all
                      </label>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {groupPermissions.map((permission) => (
                        <label
                          key={permission.id}
                          className="inline-flex items-center gap-2 text-gray-700"
                        >
                          {!isViewMode && (
                            <input
                              type="checkbox"
                              className="w-4 h-4 accent-[#c91f32]"
                              checked={selectedPermissionIds.has(permission.id)}
                              disabled={isViewMode}
                              onChange={() => togglePermission(permission.id)}
                            />
                          )}
                          <span className="capitalize">
                            {permission.action.replaceAll("_", " ")}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              },
            )
          )}
        </div>

        <div className="flex flex-wrap gap-3 mt-6">
          {/* <button className="px-6 py-3 rounded border border-gray-300 hover:bg-gray-50 transition-all duration-200" onClick={() => navigate("/roles")}>
            Cancel
          </button> */}
        </div>
      </div>
    </div>
  );
}

export default RoleForm;
